import PropTypes from "prop-types";
import {Affix, Button, Col, ConfigProvider, Menu, Row, Spin, Typography, Badge, Alert} from "antd";
import './Layout.css';
import {LeftOutlined, PhoneFilled, BellFilled} from "@ant-design/icons";
import {mainMenuItems, mobileMenuItems} from "./menuItems";
import React, {useCallback, useContext, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useScreenSizeAtAndAbove, useScreenSizeBelow} from "../../utility/hooks/useScreenSize";
import {getDriverRouteById, getDriverRouteObjectFromPath} from "../routes/driverRoutes";
import {themeLocations} from "../../theme/themeLocations";
import {getCscCompanyGroupConfig, getCscCustomerProfile} from "../../api/csc";
import {signOutUser} from "../../api/security";
import {
    getLocalData,
    getSessionData,
    storeLocalData,
    storeSessionData,
    clearSessionData
} from "../../utility/global-util";
import { iconLocations } from "../image/IconLocations";
import GlobalContext from "../../GlobalContext";
import {useTranslation} from "react-i18next";
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";

const { Text } = Typography;

const MainPhoneNumber = ({loading, phoneNumber}) => {
    return (
         loading
         ? <div style={{ paddingRight: '10px', paddingTop: '5px' }}>
               <Spin />
           </div>
         : ( phoneNumber
             ? <span>
                   <PhoneFilled className="dp-header-phone-icon" />
                   <a href={'tel:' + phoneNumber}><Text className="dp-header-phone-msg">{phoneNumber}</Text></a>
               </span>
             : <></> )
    );
}

const NotificationBadge = ({ loading, count, onClick = () => {}, style = {} }) => {
    return (
        <div style={{ ...style, paddingRight: '10px', paddingTop: '5px' }}>
            {loading
             ?  <Spin />
             :  <ConfigProvider
                    theme={{
                        components: {
                            Badge: {
                                textFontSizeSM: 10,
                            },
                        },
                    }}>
                    <Badge count={count} size="small" onClick={() => onClick()} style={{ marginTop: '6px', cursor: 'pointer' }}>
                        <BellFilled className="dp-header-bell-icon" onClick={() => onClick()} style={{ cursor: 'pointer' }} />
                    </Badge>
                </ConfigProvider>}
        </div>
    );
}

/**
 * The application header for authenticated users.
 * @param props The react props.
 */
const MainHeader = ({ permissions, notifyToggle, notifyCount, refreshNotifications }) => {

    const { t } = useTranslation();

    const { isOnline } = useOnlineStatus();

    const navigate = useNavigate();

    const location = useLocation();

    const isSimplifiedHeader = useScreenSizeBelow(1200);
    const showPhoneNoInSimplified = useScreenSizeAtAndAbove(769);

    const globalData = useContext(GlobalContext);

    const [backRouteId, setBackRouteId] = useState(null);
    const [backRouteTrans, setBackRouteTrans] = useState(null);

    // Main Contact Phone Number
    const [contactLoading, setContactLoading] = useState(0);
    const [contactPhoneNumber, setContactPhoneNumber] = useState(null);

    // Customer Contact and Logo
    const [customerLoading, setCustomerLoading] = useState(0);
    const [customerPhoneNumber, setCustomerPhoneNumber] = useState(null);
    const [customerLogoSourceData, setCustomerLogoSourceData] = useState(null);

    // Handle no allocated contracts
    const [dummyMode, setDummyMode] = useState(false);
    const allocContracts = getSessionData("driverHasAllocContracts"); //comes back as string
    useEffect(() => {
        setDummyMode(allocContracts ? allocContracts === 'false': false);
    }, [allocContracts, setDummyMode]);

    // Menu Control
    const [menuData, setMenuData] = useState([]);
    const [currentMenuItem, setCurrentMenuItem] = useState('home');
    const menuClick = useCallback((e) => {
        console.log('..clicked on menu item: ', e.key);

        if (e.hasOwnProperty('key')) {

            setCurrentMenuItem(e.key);

            // Navigate if route and menu keys different or special behaviour
            if (e.key === 'logout') {
                // LOGOUT
                storeSessionData('loginHash', '');
                globalData.setRequestedRoute(null);
                // redirect logout page for SSO or login page
                if (getSessionData('ssoProvider') != null && getSessionData('ssoDestination') != null) {
                    navigate(getDriverRouteById('finish-sso'));
                } else {
                    signOutUser()
                      .then(res => {
                      })
                      .catch(err => {
                        clearSessionData();
                      })
                      .finally(() => {
                        navigate(getDriverRouteById('start'));
                      });
                }
            }

            else if(e.hasOwnProperty('path')) {
                refreshNotifications();
                navigate(e.path);
            }
        
            // Default - route kay same as menu key
            else {
                refreshNotifications();
                navigate(getDriverRouteById(e.key));
            }
        }
    }, [setCurrentMenuItem, navigate, globalData, refreshNotifications]);

    // LOAD
    useEffect(() => {
        if(isOnline) {
            // ONLINE
            storeLocalData('coGroupContactNo', null); //unset prev set
            storeLocalData('customerContactNo', null);
            storeLocalData('customerLogoSourceData', null);
            getCscCompanyGroupConfig(setContactLoading)
                .then(res => {
                    if (res && res.contactPhoneNumber) {
                        setContactPhoneNumber(res.contactPhoneNumber);
                        storeLocalData('coGroupContactNo', res.contactPhoneNumber);
                    }
                })
                .catch(ignoreErr => {
                    console.log(ignoreErr);
                    setContactPhoneNumber(null);
                });

            const userCustomer = getSessionData('selectedCustomer');
            let userCustomerAccount = '';
            let userCustomerEntity = '';
            if (userCustomer) {
                userCustomerAccount = userCustomer.code ? userCustomer.code : '';
                userCustomerEntity = userCustomer.entityId ? userCustomer.entityId : '';
            }
            getCscCustomerProfile(userCustomerEntity, 'C', userCustomerAccount, setCustomerLoading)
                .then(res => {
                    if (res && res.phoneNumber) {
                        setCustomerPhoneNumber(res.phoneNumber);
                        storeLocalData('customerContactNo', res.phoneNumber);
                    }
                    if (res && res.logoType && res.logoData) {
                        setCustomerLogoSourceData('data:' + res.logoType + ';base64,' + res.logoData);
                        storeLocalData('customerLogoSourceData', 'data:' + res.logoType + ';base64,' + res.logoData);
                    }
                })
                .catch(ignoreErr => {
                    console.log(ignoreErr);
                });
        } else {
            // OFFLINE
            setContactPhoneNumber(getLocalData('coGroupContactNo'));
            setContactLoading(100);
            setCustomerPhoneNumber(getLocalData('customerContactNo'));
            setCustomerLogoSourceData(getLocalData('customerLogoSourceData'));
            setCustomerLoading(100);
        }
    }, [isOnline]);

    // Set current menu (if navigated by other means)
    useEffect(() => {
        const currentPath = location.pathname;
        const currentRoute = getDriverRouteObjectFromPath(currentPath);
        if (currentRoute) {
            setCurrentMenuItem(currentRoute.id)
        }
    }, [location.pathname, setCurrentMenuItem]);

    // Get optional back route ID
    useEffect(() => {
        setBackRouteId(globalData.currentBackRouteId ? globalData.currentBackRouteId : null);
        setBackRouteTrans(globalData.currentBackRouteTrans ? globalData.currentBackRouteTrans : null);
    }, [globalData.currentBackRouteId, globalData.currentBackRouteTrans]);

    // Menus
    const currentLocale = getSessionData('userLocale') ? getSessionData('userLocale').toString() : '';
    useEffect(() => {
        setTimeout(() => {
            console.log('..setting menu for: ' + currentLocale);  //** keep this log to force the menu to update on a language change
            setMenuData(isSimplifiedHeader ? mobileMenuItems({permissions, driverNotAllocated: dummyMode}) : mainMenuItems({permissions, driverNotAllocated: dummyMode}));
        }, 1000); //delay 1 second to give 118next chance to chance language
    }, [isSimplifiedHeader, currentLocale, permissions, dummyMode, isOnline]);

    // RENDER
    return (// Ant messages and notifications have z-index of 1010 so dont make this any higher than that!
        <header style={{ zIndex: '999' }}>
            <Affix offsetTop={0} className="dp-header-affix" style={ !isOnline ? { height: '70px' } : {} }>
                <Row align="middle" justify="space-between" className="dp-header-layout">
                    <Col>
                        { isSimplifiedHeader && backRouteId  //optional back button - set route in driverRoutes
                          ? <Button size="small" type="text" className="dp-header-top-back-button"
                                    onClick={() => navigate(getDriverRouteById(backRouteId))}>
                                <LeftOutlined style={{ marginRight: '0' }} /><Text>{t(backRouteTrans)}</Text>
                            </Button>
                          : <Row align="middle" className="dp-header-logo-row">
                                <Col><img src={themeLocations.MAIN_LOGO} className="dp-header-logo" alt="" /></Col>
                                <Col>
                                    { customerLoading < 100
                                      ? <Spin />
                                      : <img src={customerLogoSourceData} className="dp-header-logo" alt="" /> }
                                </Col>
                            </Row>
                        }
                    </Col>
                    <Col>
                        <img src={iconLocations.OFFLINE_ICON} className={'dp-offline-icon' + (isOnline ? '-hide' : '-show')} alt="" />
                    </Col>
                    <Col>
                    { isSimplifiedHeader //use window hook as menu would not reappear properly when sizing screen with CSS media queries
                      ?
                        <Row>
                            { showPhoneNoInSimplified
                              ? <Col>
                                    <Row>
                                        <NotificationBadge count={notifyCount} onClick={() => notifyToggle()} />
                                        <MainPhoneNumber loading={contactLoading < 100 || customerLoading < 100}
                                                         phoneNumber={customerPhoneNumber != null ? customerPhoneNumber : contactPhoneNumber} />
                                    </Row>
                                </Col>
                              : <></> }
                            <Col>
                                <div className="dp-main-menu-mobile">
                                    <ConfigProvider
                                        theme={{
                                            components: {
                                                Menu: {
                                                    /* design tokens */
                                                    iconSize: 15,
                                                    fontSize: 13,
                                                    padding: 5,
                                                    iconMarginInlineEnd: 5,
                                                    horizontalLineHeight: '36px',
                                                    horizontalItemSelectedBg: 'transparent',
                                                    horizontalItemSelectedColor: 'var(--dp-primary-colour)',
                                                    itemSelectedBg: 'var(--dp-menu-item-selected-colour)',
                                                    itemSelectedColor: 'var(--dp-menu-item-text-colour-selected)',
                                                    activeBarHeight: 0,
                                                    activeBarBorderWidth: 1,
                                                },
                                            },
                                        }}
                                    >
                                        <Menu onClick={menuClick} selectedKeys={[currentMenuItem]} mode="horizontal" items={menuData} />
                                    </ConfigProvider>
                                </div>
                            </Col>
                        </Row>
                      :
                        <div>
                            <Row align="middle" justify="end" style={{ height: '32px', marginTop: '-2px' }}>
                                <MainPhoneNumber loading={contactLoading < 100 || customerLoading < 100}
                                                 phoneNumber={customerPhoneNumber != null ? customerPhoneNumber : contactPhoneNumber} />
                            </Row>
                            <Row justify="end" className="dp-main-menu-expanded">
                                <NotificationBadge count={notifyCount} onClick={() => notifyToggle()} style={{ marginRight: '20px', marginTop: '2px' }} />
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Menu: {
                                                /* design tokens */
                                                iconSize: 15,
                                                fontSize: 13,
                                                padding: 10,
                                                iconMarginInlineEnd: 5,
                                                horizontalLineHeight: '36px',
                                                horizontalItemSelectedBg: 'var(--dp-menu-item-selected-colour)',
                                                horizontalItemSelectedColor: 'var(--dp-menu-item-text-colour-selected)',
                                                itemSelectedBg: 'var(--dp-menu-item-selected-colour)',
                                                itemSelectedColor: 'var(--dp-menu-item-text-colour-selected)',
                                                activeBarHeight: 0,
                                                activeBarBorderWidth: 1,
                                            },
                                        },
                                    }}
                                >
                                    <Menu onClick={menuClick} selectedKeys={[currentMenuItem]} mode="horizontal" items={menuData} disabledOverflow />
                                </ConfigProvider>
                            </Row>
                        </div>
                    }
                    </Col>
                </Row>
                {!isOnline
                    ? <Row align="middle" justify="center">
                        <Alert showIcon message={t('error.unable_connect')} type="warning" className="dp-top-offline-msg" />
                      </Row>
                    : <></> }
            </Affix>
        </header>
    );
}

// Validate prop type parameters.
MainHeader.propTypes = {
    companyLogo: PropTypes.string,
};

MainHeader.defaultProps = {
    companyLogo: null,
};

export default MainHeader;